<template>
  <div class="project-settings-notifications-table" data-locator="notifications-history-table">
    <template v-if="roomNotificationsError">
      <app-alert-in-page variant="error"
                         no-icon
                         :dismissible="false"
      >
        {{ $t('project.project-settings.ProjectSettingsNotifications.getNotificationsError') }}
      </app-alert-in-page>
    </template>
    <template v-else>
      <template v-if="roomNotificationsPending">
        <AppDataTableSkeletonLoader/>
      </template>
      <template v-else>
        <v-data-table-server :items="notifications"
                             :items-length="notificationsTotalCount"
                             :must-sort="true"
                             :sort-by="roomNotificationsSort"
                             :headers="headers"
                             @update:sort-by="onSortByChange"
        >
          <template #item.user_full_name="{item}">
            {{ item.userFullName }}
          </template>
          <template #item.user_email="{item}">
            {{ item.userEmail }}
          </template>
          <template #item.sent_at="{item}">
            <DateDisplay :value="item.sentAt"
                         with-hours
            />
          </template>
          <template #item.subject="{item}">
            <AppTooltip>
              <template #activator="{ props }">
                <div class="email-subject-col text-truncated" v-bind="props">
                  <span>{{ item.subject }}</span>
                </div>
              </template>
              <span>{{ item.subject }}</span>
            </AppTooltip>
          </template>
          <template #item.bounce="{item}">
            <AppTooltip v-if="item.isBounced">
                <template #activator="{ props }">
                  <app-material-icon
                    name="warning"
                    filled
                    color="icon-warning"
                    size="xs"
                    v-bind="props"
                  />
                </template>
                <span v-html="$t('project.project-settings.ProjectSettingsNotifications.emailBounceTooltip')"></span>
              </AppTooltip>
          </template>

          <template #bottom>
            <div class="d-flex justify-end ma-4">
              <AppPagination v-model:current-page="currentPage"
                             v-model:page-size="itemsPerPage"
                             :total="notificationsTotalCount"
                             @update:current-page="onPageChange"
                             @update:page-size="onItemsPerPageChange"
              />
            </div>
          </template>
        </v-data-table-server>
      </template>
    </template>
  </div>
</template>

<script>
  import { debounce } from 'lodash-es'
  import { mapActions, mapGetters, mapMutations, mapState, useStore } from 'vuex'

  import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
  import AppTooltip from '@/common/AppTooltip.vue'
  import DateDisplay from '@/common/DateDisplay.vue'
  import AppPagination from '@/common/pagination/AppPagination.vue'
  import { usePagination } from '@/common/pagination/usePagination'
  import { toast } from '@/common/utils/toast'
  import { GET_ROOM_NOTIFICATIONS } from '@/store/modules/room/action_types'
  import {
    SET_ROOM_NOTIFICATIONS_SEARCH_QUERY,
    SET_ROOM_NOTIFICATIONS_SORT,
    RESET_ROOM_NOTIFICATIONS_SORT,
  } from '@/store/modules/room/mutation_types'

  export default {
    name: 'ProjectSettingsNotificationsTable',
    components: {
      AppPagination,
      AppTooltip,
      DateDisplay,
      AppDataTableSkeletonLoader,
    },
    props: {
      mnemo: {
        type: String,
        required: true,
      },
    },
    setup: () => {
      const store = useStore()
      const { currentPage, itemsPerPage } = usePagination({
        currentUserId: store.getters['user/currentUserId'],
        userSettingsKey: 'project-settings-notifications-items-per-page',
      })

      return {
        currentPage,
        itemsPerPage,
      }
    },
    data () {
      const headersClass = 'text-text-primary body-2 font-weight-medium white-space-nowrap'
      return {
        headers: [
          {
            title: this.$t('project.project-settings.ProjectSettingsNotifications.headers.recipients'),
            class: headersClass,
            value: 'user_full_name',
            sortable: true,
          },
          {
            title: this.$t('project.project-settings.ProjectSettingsNotifications.headers.email'),
            class: headersClass,
            value: 'user_email',
            sortable: true,
          },
          {
            title: this.$t('project.project-settings.ProjectSettingsNotifications.headers.date'),
            class: headersClass,
            value: 'sent_at',
            sortable: true,
          },
          {
            title: this.$t('project.project-settings.ProjectSettingsNotifications.headers.emailSubject'),
            class: headersClass,
            value: 'subject',
            sortable: true,
          },
          {
            sortable: false,
            width: '50px',
            value: 'bounce',
          },
        ],
      }
    },
    computed: {
      ...mapState('room', [
        'roomNotificationsPending',
        'roomNotifications',
        'roomNotificationsSearchQuery',
        'roomNotificationsSort',
        'roomNotificationsError',
      ]),
      ...mapGetters('room', ['getNotificationsParams']),
      ...mapGetters('user', ['currentUserId']),
      notifications () {
        return this.roomNotifications?.data
      },
      notificationsTotalCount () {
        return this.roomNotifications?.pagination.itemCount ?? 0
      },
    },
    watch: {
      roomNotificationsSearchQuery: {
        handler () {
          this.currentPage = 1
          this.prepareGetRoomNotificationsImmediate(1)
        },
        deep: true,
      },
      roomNotificationsSort: {
        handler () {
          this.currentPage = 1
          this.prepareGetRoomNotificationsImmediate(1)
        },
        deep: true,
      },
    },
    created () {
      this.prepareGetRoomNotificationsDebounced = debounce(this.prepareGetRoomNotifications, 1000)
      this.prepareGetRoomNotificationsImmediate = this.prepareGetRoomNotifications
      this.prepareGetRoomNotificationsImmediate(1)
    },
    beforeUnmount () {
      this.SET_ROOM_NOTIFICATIONS_SEARCH_QUERY('')
      this.RESET_ROOM_NOTIFICATIONS_SORT()
    },
    methods: {
      ...mapActions('room', [GET_ROOM_NOTIFICATIONS]),
      ...mapMutations('room', [
        SET_ROOM_NOTIFICATIONS_SEARCH_QUERY,
        SET_ROOM_NOTIFICATIONS_SORT,
        RESET_ROOM_NOTIFICATIONS_SORT,
      ]),
      onSortByChange (sort) {
        this.SET_ROOM_NOTIFICATIONS_SORT(sort)
      },
      onItemsPerPageChange () {
        this.currentPage = 1
        this.prepareGetRoomNotificationsDebounced(1)
      },
      onPageChange (page) {
        this.prepareGetRoomNotificationsImmediate(page)
      },
      async prepareGetRoomNotifications (page) {
        try {
          await this.GET_ROOM_NOTIFICATIONS({
            mnemo: this.mnemo,
            params: {
              page,
              pageSize: this.itemsPerPage,
              ...this.getNotificationsParams,
            },
          })
        } catch (error) {
          toast.error(this.$t('project.project-settings.ProjectSettingsNotifications.getNotificationsError'))
        }
      },
    },
  }
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.project-settings-notifications-table {
  .email-subject-col {
    max-width: 200px;
    @media #{map-get($display-breakpoints, 'md-and-up')} {
      max-width: 600px;
    }
    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      max-width: 700px;
    }
    @media #{map-get($display-breakpoints, 'xl-and-up')} {
      max-width: 1000px;
    }
  }
}
</style>
